import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import { initOfflineDetection } from './utils/offlineDetection';
import { initSinteseOnlineRetry, flushSinteses } from './stores/sinteseStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import { flushReferencesQueue } from './services/googleDrive/referencesWriteQueue';
import { flushDestinoSync } from './services/destinoDriveSync';
import { flushAllPendingSumarioSaves } from './components/SumarioComentado/DriveSync';
import { flushPreferencesQueue } from './stores/preferencesStore';

// Capture beforeinstallprompt as early as possible (before components mount)
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as unknown as Record<string, unknown>).__installPrompt = e;
});

// Initialize Sentry error monitoring
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const hostname = window.location.hostname;
  const environment = hostname === 'bigwa.app'
    ? 'production'
    : hostname.includes('staging')
      ? 'staging'
      : 'development';

  Sentry.init({
    dsn: sentryDsn,
    environment,
    sampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
  });
}

// Flush pending debounced saves before the tab closes (eager imports — A7)
window.addEventListener('beforeunload', () => {
  flushReferencesQueue();
  flushDestinoSync();
  flushAllPendingSumarioSaves();
  flushPreferencesQueue();
  flushSinteses(); // F3 best-effort (blueprint §3); primário é visibilitychange/pagehide abaixo
});

// Flush de sínteses no teardown (F3, defesa em profundidade — Pilar 1 / AC-7, Sprint 043 C2).
// visibilitychange(hidden) é o primário: a página segue viva, então a cadeia de awaits do save
// completa e o PATCH vai ao Drive — o único que dispara de forma confiável no mobile, onde
// beforeunload/unload frequentemente nem firam. pagehide é o backstop terminal. flushSinteses só
// salva se dirty (R1) e cobre todos os projetos dirty (R5). Os 4 flushes canônicos acima seguem
// em beforeunload (migração dos quatro para visibilitychange+pagehide = follow-up R3, fora de escopo).
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushSinteses();
});
window.addEventListener('pagehide', flushSinteses);

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason?.message || String(reason);
  // Import logger dynamically to avoid circular deps at boot
  import('./utils/logger').then(m => m.logger.error('UnhandledRejection', message, reason)).catch(() => {});
});

// Initialize offline queue IndexedDB (best-effort — não bloqueia)
import('./services/offlineQueue').then(({ initOfflineQueue }) => {
  initOfflineQueue().catch(() => {}); // IDB indisponível é OK — degradação graciosa
});

// Initialize offline detection
initOfflineDetection();
initSinteseOnlineRetry();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary section="App" fallback={
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <p style={{ fontSize: '16px', color: '#333' }}>O Bigwa encontrou um erro inesperado.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '16px',
          background: '#477B72',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 32px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Recarregar página
      </button>
    </div>
  }>
    <App />
  </ErrorBoundary>
);
